import { AlertCircle, Bike, Calendar, Check, Edit, Plus, Trash2, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import useServices from '../hooks/useServices';
import './Temporadas.css';

const Temporadas = () => {
  const { seasonService, seasonModelService } = useServices();
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);
  const [showModelAssignment, setShowModelAssignment] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [assignedModels, setAssignedModels] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModels, setSelectedModels] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [seasonToDelete, setSeasonToDelete] = useState(null);
  const [deleteInfo, setDeleteInfo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    priority: 1,
    year: new Date().getFullYear()
  });

  useEffect(() => {
    loadSeasons();
  }, []);

  const loadSeasons = async () => {
    try {
      setLoading(true);
      const data = await seasonService.getAllSeasons();
      setSeasons(data);
      setError(null);
    } catch (err) {
      setError('Error al cargar las temporadas');
      console.error('Error loading seasons:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSeason) {
        await seasonService.updateSeason(editingSeason.id, formData);
      } else {
        await seasonService.createSeason(formData);
      }
      
      await loadSeasons();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar la temporada');
      console.error('Error saving season:', err);
    }
  };

  const handleEdit = (season) => {
    setEditingSeason(season);
    setFormData({
      name: season.name,
      startDate: season.startDate.split('T')[0],
      endDate: season.endDate.split('T')[0],
      priority: season.priority,
      year: season.year
    });
    setShowForm(true);
  };

  const handleDelete = async (season) => {
    try {
      // Intentar eliminar sin forzar para obtener información de elementos asociados
      await seasonService.deleteSeason(season.id, false);
    } catch (err) {
      console.log('Error response:', err);
      
      // Verificar si el error contiene información de elementos asociados
      if (err.status === 400 && err.data && err.data.hasAssociatedItems) {
        // Usar la información que viene del backend
        setDeleteInfo(err.data);
        setSeasonToDelete(season);
        setShowDeleteModal(true);
        return;
      }
      
      // Si no se puede obtener la información, mostrar error genérico
      setError(err.message || 'Error al eliminar la temporada');
      console.error('Error deleting season:', err);
    }
  };

  const confirmDelete = async () => {
    if (!seasonToDelete) return;
    
    try {
      await seasonService.deleteSeason(seasonToDelete.id, true);
      await loadSeasons();
      setShowDeleteModal(false);
      setSeasonToDelete(null);
      setDeleteInfo(null);
    } catch (err) {
      setError(err.message || 'Error al eliminar la temporada');
      console.error('Error deleting season:', err);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setSeasonToDelete(null);
    setDeleteInfo(null);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      startDate: '',
      endDate: '',
      priority: 1,
      year: new Date().getFullYear()
    });
    setEditingSeason(null);
    setShowForm(false);
  };

  // Gestión de modelos asignados
  const handleAssignModels = async (season) => {
    try {
      setSelectedSeason(season);
      setShowModelAssignment(true);
      
      // Cargar modelos asignados y disponibles
      const [assigned, available] = await Promise.all([
        seasonModelService.getSeasonModels(season.id),
        seasonModelService.getAvailableModelsForSeason(season.id)
      ]);
      
      setAssignedModels(assigned);
      setAvailableModels(available);
      setSelectedModels([]);
    } catch (err) {
      setError('Error al cargar modelos de la temporada');
      console.error('Error loading season models:', err);
    }
  };

  const handleAssignSelectedModels = async () => {
    try {
      if (selectedModels.length === 0) return;
      
      await seasonModelService.assignMultipleModelsToSeason(selectedSeason.id, selectedModels);
      
      // Recargar datos
      const [assigned, available] = await Promise.all([
        seasonModelService.getSeasonModels(selectedSeason.id),
        seasonModelService.getAvailableModelsForSeason(selectedSeason.id)
      ]);
      
      setAssignedModels(assigned);
      setAvailableModels(available);
      setSelectedModels([]);
    } catch (err) {
      setError('Error al asignar modelos');
      console.error('Error assigning models:', err);
    }
  };

  const handleUnassignModel = async (vehicleModelId) => {
    try {
      await seasonModelService.unassignModelFromSeason(selectedSeason.id, vehicleModelId);
      
      // Recargar datos
      const [assigned, available] = await Promise.all([
        seasonModelService.getSeasonModels(selectedSeason.id),
        seasonModelService.getAvailableModelsForSeason(selectedSeason.id)
      ]);
      
      setAssignedModels(assigned);
      setAvailableModels(available);
    } catch (err) {
      setError('Error al desasignar modelo');
      console.error('Error unassigning model:', err);
    }
  };

  const toggleModelSelection = (modelId) => {
    setSelectedModels(prev => 
      prev.includes(modelId) 
        ? prev.filter(id => id !== modelId)
        : [...prev, modelId]
    );
  };

  const closeModelAssignment = () => {
    setShowModelAssignment(false);
    setSelectedSeason(null);
    setAssignedModels([]);
    setAvailableModels([]);
    setSelectedModels([]);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isSeasonActive = (season) => {
    const now = new Date();
    const startDate = new Date(season.startDate);
    const endDate = new Date(season.endDate);
    return now >= startDate && now <= endDate;
  };

  if (loading) {
    return (
      <div className="temporadas-container">
        <div className="loading">Cargando temporadas...</div>
      </div>
    );
  }

  return (
    <div className="temporadas-container">
      <div className="temporadas-header">
        <h1>Temporadas</h1>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
        >
          <Plus size={20} />
          Nueva Temporada
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingSeason ? 'Editar Temporada' : 'Nueva Temporada'}</h2>
              <button 
                className="btn-close"
                onClick={resetForm}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="season-form">
              <div className="form-group">
                <label htmlFor="name">Nombre de la temporada</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  placeholder="Ej: Temporada Alta 2024"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="startDate">Fecha de inicio</label>
                  <input
                    type="date"
                    id="startDate"
                    value={formData.startDate}
                    onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="endDate">Fecha de fin</label>
                  <input
                    type="date"
                    id="endDate"
                    value={formData.endDate}
                    onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="priority">Prioridad</label>
                  <input
                    type="number"
                    id="priority"
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value)})}
                    min="1"
                    max="100"
                    required
                  />
                  <small>Mayor número = mayor prioridad</small>
                </div>

                <div className="form-group">
                  <label htmlFor="year">Año</label>
                  <input
                    type="number"
                    id="year"
                    value={formData.year}
                    onChange={(e) => setFormData({...formData, year: parseInt(e.target.value)})}
                    min="2020"
                    max="2030"
                    required
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingSeason ? 'Actualizar' : 'Crear'} Temporada
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="seasons-grid">
        {seasons.length === 0 ? (
          <div className="empty-state">
            <Calendar size={48} />
            <h3>No hay temporadas creadas</h3>
            <p>Crea tu primera temporada para comenzar a gestionar precios estacionales.</p>
          </div>
        ) : (
          seasons.map((season) => (
            <div key={season.id} className={`season-card ${isSeasonActive(season) ? 'active' : ''}`}>
              <div className="season-header">
                <h3>{season.name}</h3>
                {isSeasonActive(season) && (
                  <span className="active-badge">Activa</span>
                )}
              </div>
              
              <div className="season-details">
                <div className="season-date">
                  <strong>Período:</strong>
                  <span>{formatDate(season.startDate)} - {formatDate(season.endDate)}</span>
                </div>
                
                <div className="season-info">
                  <div className="info-item">
                    <strong>Año:</strong> {season.year}
                  </div>
                  <div className="info-item">
                    <strong>Prioridad:</strong> {season.priority}
                  </div>
                </div>
              </div>

              <div className="season-actions">
                <button 
                  className="btn btn-sm btn-primary"
                  onClick={() => handleAssignModels(season)}
                >
                  <Bike size={16} />
                  Modelos
                </button>
                <button 
                  className="btn btn-sm btn-secondary"
                  onClick={() => handleEdit(season)}
                >
                  <Edit size={16} />
                  Editar
                </button>
                <button 
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDelete(season)}
                >
                  <Trash2 size={16} />
                  Eliminar
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal de gestión de modelos */}
      {showModelAssignment && selectedSeason && (
        <div className="modal-overlay">
          <div className="modal model-assignment-modal">
            <div className="modal-header">
              <h2>Gestionar Modelos - {selectedSeason.name}</h2>
              <button 
                className="btn-close"
                onClick={closeModelAssignment}
              >
                ×
              </button>
            </div>
            
            <div className="model-assignment-content">
              {/* Modelos asignados */}
              <div className="assigned-models-section">
                <h3>Modelos Asignados ({assignedModels.length})</h3>
                <div className="models-list">
                  {assignedModels.length === 0 ? (
                    <p className="empty-message">No hay modelos asignados</p>
                  ) : (
                    assignedModels.map((assignment) => (
                      <div key={assignment.id} className="model-item assigned">
                        <div className="model-info">
                          <strong>{assignment.vehicleModel.name}</strong>
                          <span className="category">{assignment.vehicleModel.category.name}</span>
                        </div>
                        <button 
                          className="btn btn-sm btn-danger"
                          onClick={() => handleUnassignModel(assignment.vehicleModelId)}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Modelos disponibles */}
              <div className="available-models-section">
                <h3>Modelos Disponibles ({availableModels.length})</h3>
                <div className="models-list">
                  {availableModels.length === 0 ? (
                    <p className="empty-message">Todos los modelos están asignados</p>
                  ) : (
                    availableModels.map((model) => (
                      <div 
                        key={model.id} 
                        className={`model-item selectable ${selectedModels.includes(model.id) ? 'selected' : ''}`}
                        onClick={() => toggleModelSelection(model.id)}
                      >
                        <div className="model-info">
                          <strong>{model.name}</strong>
                          <span className="category">{model.category.name}</span>
                        </div>
                        {selectedModels.includes(model.id) && (
                          <Check size={16} className="check-icon" />
                        )}
                      </div>
                    ))
                  )}
                </div>
                
                {selectedModels.length > 0 && (
                  <div className="assign-actions">
                    <button 
                      className="btn btn-primary"
                      onClick={handleAssignSelectedModels}
                    >
                      Asignar {selectedModels.length} modelo{selectedModels.length > 1 ? 's' : ''}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal delete-confirmation-modal">
            <div className="modal-header">
              <h3>⚠️ Confirmar Eliminación</h3>
            </div>
            
            <div className="modal-body">
              <div className="delete-warning">
                <p>
                  <strong>¿Estás seguro de que quieres eliminar la temporada "{seasonToDelete?.name}"?</strong>
                </p>
                
                {deleteInfo?.associatedItems && (
                  <div className="associated-items">
                    <p>Esta temporada tiene elementos asociados que también se eliminarán:</p>
                    
                    {deleteInfo.associatedItems.seasonPrices > 0 && (
                      <div className="associated-section">
                        <h4>💰 Precios Estacionales ({deleteInfo.associatedItems.seasonPrices})</h4>
                        <ul className="associated-list">
                          {deleteInfo.associatedItems.seasonPricesDetails.map((price, index) => (
                            <li key={index}>
                              <strong>{price.modelName}</strong> ({price.category}) - €{price.basePrice}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {deleteInfo.associatedItems.modelAssignments > 0 && (
                      <div className="associated-section">
                        <h4>🚲 Modelos Asignados ({deleteInfo.associatedItems.modelAssignments})</h4>
                        <ul className="associated-list">
                          {deleteInfo.associatedItems.modelAssignmentsDetails.map((assignment, index) => (
                            <li key={index}>
                              <strong>{assignment.modelName}</strong> ({assignment.category})
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="delete-consequences">
                  <p><strong>⚠️ Esta acción no se puede deshacer.</strong></p>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={cancelDelete}
              >
                Cancelar
              </button>
              <button 
                className="btn btn-danger" 
                onClick={confirmDelete}
              >
                Eliminar Todo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Temporadas;
